import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';

const Select = ({
  label,
  id,
  value,
  onChange = () => ({}),
  onBlur = () => ({}),
  options,
  disabled = false,
  required = false,
  inline = false,
  searchable = false,
  dataTest,
  error,
  bottomMarginSizing,
  isMulti = false
}) => {
  let wrapperClassName;
  let errorClassName;
  let containerClassName = 'grow focus:outline-none focus:border-purple-100';
  let labelClassName = 'block font-sans text-sm font-bold text-gray-600 mb-1';
  if (inline) {
    wrapperClassName = `flex flex-wrap mb-1 items-center gap-x-4 w-full`;
    labelClassName = `${labelClassName} w-24 text-right`;
    containerClassName = `${containerClassName} w-3/5`;
  } else {
    wrapperClassName = `block ${
      bottomMarginSizing === 1 ? 'mb-4' : bottomMarginSizing === 0 ? 'mb-0' : 'mb-8'
    }`;
  }

  if (error) {
    labelClassName = `${labelClassName} text-red-100`;
    errorClassName =
      'block w-full ml-[112px] font-medium tracking-wide text-red-500 text-xs mt-1 mb-2';
  } else {
    errorClassName = 'font-medium tracking-wide text-red-500 text-xs mt-1 ml-1';
  }

  return (
    <div className={wrapperClassName}>
      <label id={`${id}-label`} className={labelClassName}>
        {label}
        {required ? <span className="text-blue-100">*</span> : null}
      </label>
      <ReactSelect
        id={id}
        inputId={id}
        name={id}
        isMulti={isMulti}
        classNames={{
          container: () => containerClassName,
          control: () =>
            'rounded-sm border-2 border-grey-200 bg-white shadow-sm px-4 py-1 hover:border-grey-300 outline-none ring-inset',
          valueContainer: () => 'font-sans text-base font-normal text-black/85 -my-0.5 -mx-2',
          input: () => 'focus:border-purple-100',
          dropdownIndicator: () => '-m-2'
        }}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderWidth: '2px',
            boxShadow: 'none',
            borderColor: error ? '#E0091F' : '#C4CFDA',
            ':hover': {
              borderColor: '#43494F'
            },
            ':focus-within': {
              borderColor: '#552EEA'
            },
            ':disabled': {
              backgroundColor: '#F6F8FA',
              borderColor: '#C4CFDA',
              color: '#25292E'
            }
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            marginTop: '2px',
            borderRadius: '2px',
            boxShadow:
              '0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12)'
          }),
          indicatorSeparator: (baseStyles) => ({
            ...baseStyles,
            display: 'none'
          }),
          input: (baseStyles) => ({
            ...baseStyles,
            'input:focus': {
              boxShadow: 'none'
            }
          }),
          option: (baseStyles, { isSelected, isFocused }) => ({
            ...baseStyles,
            backgroundColor: isSelected ? '#552EEA' : isFocused ? '#F6F8FA' : '#FFFFFF'
          })
        }}
        value={
          (isMulti
            ? options.filter((option) => value.indexOf(option.value) >= 0)
            : options.find((option) => option?.value === value)) ?? null
        }
        onChange={(values) => onChange(values?.value ?? null)}
        onBlur={onBlur}
        required={required}
        isDisabled={disabled}
        options={options}
        placeholder="Select"
        isSearchable={searchable}
        aria-label={`${id}-label`}
        aria-labelledby={`${id}-label`}
        data-testid={dataTest}
      />
      {error ? (
        <small data-testid={`${dataTest}-error`} className={errorClassName}>
          {error}
        </small>
      ) : null}
    </div>
  );
};

Select.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ).isRequired,
  dataTest: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  inline: PropTypes.bool,
  searchable: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  bottomMarginSizing: PropTypes.oneOf([1, 0, undefined])
};

export default Select;
